import React, { useCallback, useEffect, useMemo, useState } from "react";
import { LineChart } from "@mui/x-charts";
import { Box, Paper, Slider, Stack, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery, useTheme } from "@mui/material";
import PropTypes from "prop-types";
import LoadingOverlay from "../../../../../Shared/LoadingOverlay";
import ChartEmptyState from "../../../../../Shared/chartEmptyState";
import vendorStatsService from "../../../../../../services/vendorStatsService";
import { useDashboardChartsDate } from "../../context/dashboardChartsDateContext";

const DEFAULT_TRAFFIC_RANGE_POINTS = 10;
const TRAFFIC_SERIES_FETCH_LIMIT = 60;

function defaultRangeIndices(len) {
  if (len <= 0) {
    return [0, 0];
  }
  if (len <= DEFAULT_TRAFFIC_RANGE_POINTS) {
    return [0, len - 1];
  }
  return [len - DEFAULT_TRAFFIC_RANGE_POINTS, len - 1];
}

function formatPeriodLabel(period, resolution) {
  if (!period) return "";
  if (resolution === "month" && /^\d{4}-\d{2}$/.test(period)) {
    const [yy, mm] = period.split("-");
    return `${mm}/${yy}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(period)) {
    const [, mm, dd] = period.split("-");
    return `${dd}/${mm}`;
  }
  return period;
}

function formatPeriodTooltip(period, resolution) {
  if (!period) return "";
  if (resolution === "month" && /^\d{4}-\d{2}$/.test(period)) {
    const [yy, mm] = period.split("-");
    return `${mm}/${yy}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(period)) {
    const [yy, mm, dd] = period.split("-");
    return `${dd}/${mm}/${yy}`;
  }
  return period;
}

function createTickLabelInterval(visibleCount, maxTicks) {
  if (visibleCount <= 0) {
    return () => false;
  }
  if (visibleCount === 1) {
    return () => true;
  }
  const cap = Math.max(2, Math.min(maxTicks, visibleCount));
  const step = Math.max(1, Math.ceil((visibleCount - 1) / (cap - 1)));
  return (_value, i) => i === 0 || i === visibleCount - 1 || i % step === 0;
}

function TrafficItemTooltip({ itemData, series, classes, periods }) {
  const idx = itemData?.dataIndex;
  if (idx == null || idx < 0 || !periods[idx]) return null;
  const clicks = Number(series.data[idx]) || 0;
  return (
    <Paper
      elevation={4}
      className={classes?.paper}
      sx={{
        px: 1.5,
        py: 1,
        borderRadius: "10px",
        border: "1px solid rgba(15, 23, 42, 0.08)",
        pointerEvents: "none",
      }}
    >
      <Typography sx={{ fontFamily: "Rubik", fontSize: 13, color: "#334155", fontWeight: 600 }}>
        {periods[idx].tooltipDate}
      </Typography>
      <Typography sx={{ fontFamily: "Rubik", fontSize: 13, color: "#334155", mt: 0.25 }}>
        <Box component="span" sx={{ color: "#2e7d32", fontWeight: 700 }}>
          {clicks}
        </Box>{" "}
        Interazioni
      </Typography>
    </Paper>
  );
}

TrafficItemTooltip.propTypes = {
  itemData: PropTypes.object,
  series: PropTypes.object.isRequired,
  classes: PropTypes.object,
  periods: PropTypes.arrayOf(
    PropTypes.shape({
      xLabel: PropTypes.string.isRequired,
      tooltipDate: PropTypes.string.isRequired,
    })
  ).isRequired,
};

const TRAFFIC_CHART_HEIGHT_COMPACT = 280;
const TRAFFIC_CHART_HEIGHT_EXPANDED = 420;

function TrafficLineChart({ points, resolution, compactAxis, chartHeight = TRAFFIC_CHART_HEIGHT_COMPACT }) {
  const periods = useMemo(
    () =>
      points.map((p) => ({
        xLabel: formatPeriodLabel(p.period, resolution),
        tooltipDate: formatPeriodTooltip(p.period, resolution),
      })),
    [points, resolution]
  );

  const xLabels = useMemo(() => periods.map((p) => p.xLabel), [periods]);
  const yValues = useMemo(() => points.map((p) => Number(p.clickCount) || 0), [points]);

  const maxTicks = compactAxis ? 5 : 8;
  const tickLabelInterval = useMemo(
    () => createTickLabelInterval(points.length, maxTicks),
    [points.length, maxTicks]
  );

  const tiltLabels = points.length > maxTicks;

  const ItemTooltip = useCallback(
    (props) => <TrafficItemTooltip {...props} periods={periods} />,
    [periods]
  );

  return (
    <Box sx={{ width: "100%", minHeight: chartHeight - 10 }}>
      <LineChart
        height={chartHeight}
        margin={{ left: 22, right: 40, top: 10, bottom: tiltLabels ? 48 : 32 }}
        series={[
          {
            type: "line",
            data: yValues,
            color: "#0b3d87",
            showMark: true,
            curve: "monotoneX",
          },
        ]}
        xAxis={[
          {
            scaleType: "point",
            data: xLabels,
            tickLabelStyle: {
              fontFamily: "Rubik",
              fontSize: 11,
              fill: "#64748b",
              ...(tiltLabels ? { angle: 42, dominantBaseline: "hanging", textAnchor: "start" } : {}),
            },
            tickLabelInterval,
          },
        ]}
        yAxis={[
          {
            scaleType: "linear",
            label: "",
            tickLabelStyle: { fontFamily: "Rubik", fontSize: 11, fill: "#64748b" },
            tickSize: 4,
            disableLine: true,
          },
        ]}
        grid={{ horizontal: true, vertical: false }}
        tooltip={{ trigger: "item" }}
        slots={{ itemContent: ItemTooltip }}
        axisHighlight={{ x: "line", y: "none" }}
        sx={{
          "& .MuiLineElement-root": { strokeWidth: 2.5 },
        }}
      />
    </Box>
  );
}

TrafficLineChart.propTypes = {
  points: PropTypes.arrayOf(
    PropTypes.shape({
      period: PropTypes.string.isRequired,
      clickCount: PropTypes.number,
    })
  ).isRequired,
  resolution: PropTypes.oneOf(["day", "month"]).isRequired,
  compactAxis: PropTypes.bool,
  chartHeight: PropTypes.number,
};

const DailyTrafficChart = ({ expandedView = false }) => {
  const theme = useTheme();
  const compactAxis = useMediaQuery(theme.breakpoints.down("sm"));
  const { customRange } = useDashboardChartsDate();
  const [resolution, setResolution] = useState("month");
  const [allPoints, setAllPoints] = useState([]);
  const [rangeIndex, setRangeIndex] = useState([0, 0]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const req = { resolution };
      if (customRange?.start && customRange?.end) {
        req.startDate = customRange.start.format("YYYY-MM-DD");
        req.endDate = customRange.end.format("YYYY-MM-DD");
      } else {
        req.limit = TRAFFIC_SERIES_FETCH_LIMIT;
      }
      const res = await vendorStatsService.getVendorTraffic(req);
      setAllPoints(res.points ?? []);
    } catch {
      setAllPoints([]);
    } finally {
      setLoading(false);
    }
  }, [resolution, customRange]);

  useEffect(() => {
    load();
  }, [load]);

  const customRangeStartKey = customRange?.start?.format?.("YYYY-MM-DD") ?? "";
  const customRangeEndKey = customRange?.end?.format?.("YYYY-MM-DD") ?? "";

  useEffect(() => {
    if (loading) return;
    const len = allPoints.length;
    setRangeIndex(defaultRangeIndices(len));
  }, [loading, allPoints.length, resolution, customRangeStartKey, customRangeEndKey]);

  const indexMax = Math.max(0, allPoints.length - 1);

  const sliderValue = useMemo(() => {
    const [a, b] = rangeIndex;
    const lo = Math.max(0, Math.min(a, b, indexMax));
    const hi = Math.max(lo, Math.min(Math.max(a, b), indexMax));
    return [lo, hi];
  }, [rangeIndex, indexMax]);

  const visiblePoints = useMemo(() => {
    if (allPoints.length === 0) return [];
    const [start, end] = sliderValue;
    return allPoints.slice(start, end + 1);
  }, [allPoints, sliderValue]);

  const hasClickData = useMemo(() => allPoints.some((p) => Number(p.clickCount) > 0), [allPoints]);

  const showSeriesControls = !(!loading && !hasClickData);

  const showRangeSlider = !loading && hasClickData && allPoints.length > 1;
  const chartHeight = expandedView ? TRAFFIC_CHART_HEIGHT_EXPANDED : TRAFFIC_CHART_HEIGHT_COMPACT;
  const emptyStateMinHeight = expandedView ? 360 : 260;

  const thumbLabelFormat = useCallback(
    (idx) => {
      const i = Number(idx);
      if (i < 0 || !allPoints[i]) return "";
      return formatPeriodLabel(allPoints[i].period, resolution);
    },
    [allPoints, resolution]
  );

  const handleRangeChange = useCallback(
    (_e, value) => {
      if (!Array.isArray(value) || value.length !== 2) return;
      const [rawA, rawB] = value;
      const a = Math.round(Number(rawA));
      const b = Math.round(Number(rawB));
      const lo = Math.min(a, b);
      const hi = Math.max(a, b);
      const clampedLo = Math.max(0, Math.min(lo, indexMax));
      const clampedHi = Math.max(clampedLo, Math.min(hi, indexMax));
      setRangeIndex([clampedLo, clampedHi]);
    },
    [indexMax]
  );

  return (
    <Box sx={{ position: "relative", minHeight: 200 }}>
      <LoadingOverlay open={loading} />
      <Stack spacing={2}>
        {showSeriesControls && (
          <Stack direction="row" justifyContent="flex-end" alignItems="center" flexWrap="wrap" useFlexGap spacing={1}>
            <ToggleButtonGroup
              size="small"
              value={resolution}
              exclusive
              onChange={(_e, v) => v != null && setResolution(v)}
              aria-label="Risoluzione temporale"
              sx={{ "& .MuiToggleButton-root": { fontFamily: "Rubik", textTransform: "none", px: 1.5, m: 0 } }}
            >
              <ToggleButton value="day" sx={{ height: 28 }}>
                Giorno
              </ToggleButton>
              <ToggleButton value="month" sx={{ height: 28 }}>
                Mese
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        )}

        {!loading && !hasClickData ? (
          <ChartEmptyState minHeight={emptyStateMinHeight} />
        ) : !loading ? (
          <>
            <TrafficLineChart
              points={visiblePoints}
              resolution={resolution}
              compactAxis={compactAxis}
              chartHeight={chartHeight}
            />
            {showRangeSlider ? (
              <Box sx={{ pl: 3, pr: 7 }}>
                <Slider
                  size="small"
                  value={sliderValue}
                  min={0}
                  max={indexMax}
                  step={1}
                  onChange={handleRangeChange}
                  valueLabelDisplay="auto"
                  valueLabelFormat={thumbLabelFormat}
                  sx={{
                    color: "#0b3d87",
                    "& .MuiSlider-thumb": { width: 10, height: 10 },
                  }}
                  aria-label="Intervallo di periodi da visualizzare"
                  getAriaValueText={(v) => thumbLabelFormat(v)}
                />
              </Box>
            ) : null}
          </>
        ) : null}
      </Stack>
    </Box>
  );
};

DailyTrafficChart.propTypes = {
  expandedView: PropTypes.bool,
};

export default DailyTrafficChart;
